// Guest landing page — /<slug>
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Image as ImageIcon, ShieldCheck, Link2, Copy, UploadCloud, Bell } from "lucide-react";
import { Share2 } from "lucide-react";
import PageShell from "../components/PageShell";
import MeyecamLogo from "../components/MeyecamLogo";
import PasscodeGate from "../components/PasscodeGate";
import { Slogan } from "../components/Slogan";
import { Albums, Activity, API } from "../lib/api";
import { getIconConfig } from "../lib/eventIcons";
import { useAuth } from "../lib/AuthContext";
import { getGuestId } from "../lib/guestId";
import useDocumentMeta from "../hooks/useDocumentMeta";
import useAutoJoin from "../hooks/useAutoJoin";

const NAME_KEY = "meyecam.display_name";
const SEEN_KEY = (slug, ident) => `meyecam.activity_seen.${slug}.${ident}`;

export default function GuestLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const [album, setAlbum] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [unreadActivity, setUnreadActivity] = useState(0);
  const [myName, setMyName] = useState("");

  useAutoJoin(album); // signed-in non-owners get this album on their dashboard

  // Inject per-album OG meta tags so link previews look great on iMessage /
  // WhatsApp / Slack / Discord — title is the album name, image is the host's
  // chosen cover (or a random photo if they haven't picked one).
  useDocumentMeta({
    title: album ? `${album.name} — mEYEcam` : "mEYEcam",
    description: album
      ? `${album.host_name ? `${album.host_name} invited you to ` : "You're invited to "}share photos & videos from "${album.name}" on mEYEcam. No app, no sign-up.`
      : "A live photo & video collection for your event.",
    image: album ? `${API}/albums/${album.slug}/og-image` : undefined,
    imageWidth: 1200,
    imageHeight: 630,
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  useEffect(() => {
    (async () => {
      try {
        const a = await Albums.get(slug);
        setAlbum(a);
        setStatus("ok");
      } catch (e) {
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
  }, [slug]);

  // If the signed-in user is the host of this album, skip the guest landing
  // and drop them straight into the gallery (they don't need to upload the
  // first photo or hit "View gallery & download"; it's their album).
  useEffect(() => {
    if (status !== "ok" || !album || !user) return;
    if (album.owner_id && album.owner_id === user.user_id) {
      navigate(`/${slug}/gallery`, { replace: true });
    }
  }, [status, album, user, slug, navigate]);

  useEffect(() => {
    const n = (localStorage.getItem(NAME_KEY) || "").trim();
    setMyName(n);
    if (status !== "ok") return;
    const gid = getGuestId();
    if (!n && !gid) return;
    const ident = gid || n.toLowerCase();
    const since = localStorage.getItem(SEEN_KEY(slug, ident)) || undefined;
    Activity.for(slug, n || undefined, since, gid)
      .then((d) => setUnreadActivity((d.items || []).length))
      .catch(() => setUnreadActivity(0));
  }, [slug, status]);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
    toast.success("Share link copied");
  };

  const shareLink = async () => {
    const url = `${window.location.origin}/${slug}`;
    const title = album ? `${album.name} — mEYEcam` : "mEYEcam";
    const text = album
      ? `${album.host_name || "Your host"} invited you to share photos & videos from ${album.name}. No sign-up required.`
      : "Share photos & videos from this event on mEYEcam.";
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    copyLink();
  };

  const onCreateYourOwn = () => {
    if (user) navigate("/");
    else signIn();
  };

  if (status === "loading") {
    return (
      <PageShell>
        <div className="flex justify-center mt-24"><div className="meye-spinner" /></div>
      </PageShell>
    );
  }

  if (status === "notfound") {
    return (
      <PageShell>
        <div className="glass-card p-8 mt-12 text-center">
          <div className="text-xl font-semibold mb-2">This event link isn&apos;t active</div>
          <div className="text-sm mb-5" style={{ color: "rgba(255,255,255,0.6)" }}>
            Double-check the link, or create your own.
          </div>
          <Link to="/" className="text-sm font-semibold" style={{ color: "#22E2FF" }} data-testid="create-your-own-link">
            Create your own event →
          </Link>
        </div>
      </PageShell>
    );
  }

  // Passcode gate — album exists but requires a passcode we don't have (yet).
  // The locked response strips owner_id + cover, so the presence of
  // requires_passcode without owner_id is a reliable "locked" signal.
  if (album?.requires_passcode && !album.owner_id) {
    return (
      <PasscodeGate
        album={album}
        onUnlocked={async () => {
          try {
            const fresh = await Albums.get(slug);
            setAlbum(fresh);
          } catch { /* the token is now cached; UI will re-render on next fetch */ }
        }}
      />
    );
  }

  const cfg = getIconConfig(album.icon);
  const Icon = cfg.Icon;

  return (
    <PageShell
      headerRight={
        <div className="flex items-center gap-2">
          {myName && (
            <Link
              to={`/${slug}/activity`}
              className="meye-chip relative"
              data-testid="activity-bell"
              style={{ padding: "8px 10px" }}
              title="Your notifications"
            >
              <Bell size={14} />
              {unreadActivity > 0 && (
                <span
                  className="absolute"
                  data-testid="activity-bell-badge"
                  style={{
                    top: -4, right: -4,
                    background: "#FF1F6B",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 999,
                    padding: "1px 6px",
                    border: "1.5px solid #000",
                    minWidth: 18,
                    textAlign: "center",
                  }}
                >
                  {unreadActivity > 99 ? "99+" : unreadActivity}
                </span>
              )}
            </Link>
          )}
          <Link
            to={`/${slug}/gallery`}
            className="meye-chip"
            data-testid="gallery-chip"
            style={{ padding: "8px 12px" }}
          >
            <ImageIcon size={14} /> <span className="text-sm">Gallery</span>
          </Link>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center" style={{ paddingTop: 8 }}>
        <MeyecamLogo size={220} />
        <div
          className="font-light tracking-tight mt-1"
          style={{ fontSize: 38, lineHeight: 1.1 }}
          data-testid="event-title"
        >
          {album.name}
        </div>

        <Slogan size="sm" compact className="mt-3" />

        {/* Icon bubble */}
        <div
          className="flex items-center justify-center mt-6"
          style={{
            width: 64, height: 64, borderRadius: 999,
            border: `1.5px solid ${cfg.color}`,
            background: "rgba(255,255,255,0.03)",
          }}
          data-testid="event-icon-bubble"
        >
          <Icon size={28} color={cfg.color} />
        </div>

        <div
          className="mt-5"
          style={{ color: "rgba(255,255,255,0.78)", fontSize: 16, lineHeight: 1.5, maxWidth: 380 }}
        >
          Upload your photos and videos<br />
          to the shared {cfg.label.toLowerCase()} album.
        </div>

        <Link
          to={`/${slug}/upload`}
          className="cta-pill mt-7"
          style={{ width: "calc(100% - 22px)" }}
          data-testid="upload-cta"
        >
          <UploadCloud size={20} />
          <span>Upload Photos & Videos</span>
        </Link>

        <Link
          to={`/${slug}/gallery`}
          className="meye-chip mt-3"
          style={{ width: "calc(100% - 22px)", justifyContent: "center", background: "rgba(34,226,255,0.06)", borderColor: "rgba(34,226,255,0.35)" }}
          data-testid="view-gallery-cta"
        >
          <ImageIcon size={16} color="#22E2FF" />
          <span style={{ color: "#22E2FF" }}>View gallery & download</span>
        </Link>

        <div className="flex items-center gap-2 mt-4" style={{ color: "rgba(255,255,255,0.65)", fontSize: 14 }}>
          <ShieldCheck size={16} color="#7F4BFF" />
          <span>No sign-up required</span>
        </div>

        {/* URL share row */}
        <div
          className="mt-6 w-full flex items-stretch gap-2 px-3 py-2 rounded-2xl"
          style={{
            border: "1px solid rgba(157,78,221,0.4)",
            background: "linear-gradient(180deg, rgba(127,75,255,0.12), rgba(127,75,255,0.04))",
          }}
          data-testid="share-url-card"
        >
          <button
            onClick={copyLink}
            className="flex items-center gap-2 min-w-0 flex-1 px-1 text-left"
            data-testid="copy-url-card"
            title="Tap to copy"
            type="button"
          >
            <Link2 size={16} color="#7F4BFF" />
            <span className="truncate text-sm" style={{ color: "rgba(255,255,255,0.92)" }}>
              mEYEcam.com/{slug}
            </span>
          </button>
          <button
            onClick={copyLink}
            className="px-2 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
            aria-label="Copy link"
            data-testid="copy-url-btn"
            type="button"
          >
            <Copy size={14} color="rgba(255,255,255,0.85)" />
          </button>
          <button
            onClick={shareLink}
            className="inline-flex items-center gap-1.5 px-3 rounded-lg text-sm font-semibold"
            style={{
              background: "linear-gradient(90deg, #7F4BFF 0%, #4D1EFF 50%, #22E2FF 100%)",
              color: "#fff",
              boxShadow: "0 12px 30px -10px rgba(127,75,255,0.6)",
            }}
            aria-label="Share link"
            data-testid="share-url-btn"
            type="button"
          >
            <Share2 size={14} />
            <span>Share</span>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
            Hosting your own event?
          </div>
          <button
            onClick={onCreateYourOwn}
            className="mt-1 font-bold text-[15px]"
            style={{ color: "#22E2FF" }}
            data-testid="create-your-own-cta"
          >
            Create your own Event →
          </button>
        </div>
      </div>
    </PageShell>
  );
}
